import { Card, Grid, GridColumn, GridRow, Select, SelectOptionType } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import {
  SolverEngine,
  useEnabledSolverEnginesQuery,
  useSetEnabledSolverEnginesMutation,
} from '../../types/planung-graphql-client-defs';
import { solverEngineLabelKey } from '../../utils/timetable/solverDiagnostics';
import { showSuccessSaveToast, showUserErrorToast } from '../../utils/toast';

// All solver engines an omni-admin can hand out. Order = display order + stored order.
// ASC/Untis waren die Offline-Solver und sind abgeschafft: nicht mehr auswählbar, und ein
// noch gespeicherter Alt-Eintrag verschwindet beim nächsten Speichern.
const ALL_ENGINES: SolverEngine[] = [SolverEngine.Mathplan, SolverEngine.Timefold];

/**
 * Edits the enabled solver engines of the *currently selected* school only — the user has no
 * proper access to other organizations, so we never list or mutate them here.
 */
export const SolverInterfaceSettings = observer(() => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const context = useMemorizedCacheTag('ORGANIZATION');

  const organization = pimAuthClaims.getOrganization();

  const [{ data }] = useEnabledSolverEnginesQuery({ context });
  const [, updateEnabledSolverEngines] = useSetEnabledSolverEnginesMutation();

  const enabledEngines = useMemo(() => {
    const stored = data?.enabledSolverEngines ?? [];
    return stored.filter((engine) => ALL_ENGINES.includes(engine));
  }, [data]);

  const engineLabel = useCallback((engine: SolverEngine): string => t(solverEngineLabelKey(engine)), [t]);

  const engineOptions: SelectOptionType[] = useMemo(
    () => ALL_ENGINES.map((engine) => ({ value: engine, label: engineLabel(engine) })),
    [engineLabel],
  );

  const handleChange = useCallback(
    async (selected: MultiValue<SelectOptionType>) => {
      // Keep the canonical order so the stored list is stable regardless of click order.
      const engines = ALL_ENGINES.filter((engine) => selected.some((option) => option.value === engine));
      // Die Antwort trägt keinen Typnamen; das Cache-Tag lässt die Abfragen der freigeschalteten Solver neu laden.
      const result = await updateEnabledSolverEngines({ engines }, context);
      if (!result || result.error) {
        showUserErrorToast({ error: result?.error });
      } else {
        showSuccessSaveToast();
      }
    },
    [updateEnabledSolverEngines, context],
  );

  return (
    <Grid>
      <GridRow>
        <GridColumn width={6}>
          <Card header={{ headline: t('solver.admin.enabledEngines'), subHeadline: organization.name }}>
            <Select
              name='enabled-engines'
              isMulti
              isClearable
              menuPosition='fixed'
              label={t('solver.admin.enabledEngines')}
              placeholder={t('solver.admin.placeholder')}
              options={engineOptions}
              value={enabledEngines.map((engine) => ({ value: engine, label: engineLabel(engine) }))}
              onChange={(option) => {
                void handleChange(option as MultiValue<SelectOptionType>);
              }}
            />
          </Card>
        </GridColumn>
      </GridRow>
    </Grid>
  );
});
